import { inject, Injectable } from '@angular/core';
import { assert, isDefined } from '@sama/js-core';
import { InjectableSuperclass } from '@sama/ng-core';
import { KeyboardEventTrackingValue } from '../../../team-apis/core/event-tracking';
import { getOutputByName } from '../../h3ml/dsl/sensor-fusion/sensor-fusion-output.utils';
import { TaskWorkspaceStore } from '../../h3ml/state/task-workspace-store';
import { FusionManagerFactory } from '../../outputs/annotation/annotation-shared/fusion/fusion-manager-factory.service';
import { ObjectScopingService } from '../misc-services/object-scoping.service';
import { RootKeyboardShortcutService } from './root-keyboard-shortcut.service';

@Injectable()
export class EscapeKeyboardShortcutService extends InjectableSuperclass {
  private taskWorkspaceStore = inject(TaskWorkspaceStore);
  private fusionManagerFactory = inject(FusionManagerFactory);
  private keyboardShortcutService = inject(RootKeyboardShortcutService);
  private objectScopingService = inject(ObjectScopingService);

  register() {
    this.subscribeTo(
      this.keyboardShortcutService.getEvent$('cancelTool'),
      ({ eventTrackingValue }) => {
        const isInCanvasMode =
          this.getMinionForSelectedOutput().isInteractionMode('canvas');
        const hasSelectedShapes =
          this.getMinionForSelectedOutput().selectionService.getSelectedShapeIndexes()
            .length;
        this.getMinionForSelectedOutput().contentConnector.cancelTool(
          eventTrackingValue,
        );
        this.resetSelection(eventTrackingValue);

        if (isInCanvasMode || hasSelectedShapes) {
          return;
        }
        this.getMinionForSelectedOutput().closeZoomPanel('shortcut');
      },
    );
  }

  private getSelectedOutput() {
    const { project, selectedOutputName } = this.taskWorkspaceStore.state();
    return getOutputByName(project, selectedOutputName);
  }

  private getMinionForSelectedOutput() {
    const selectedOutputId = this.getSelectedOutput().id;
    return this.fusionManagerFactory
      .getManager(selectedOutputId)
      .getMinion(selectedOutputId)!;
  }

  private resetSelection(eventTrackingValue: KeyboardEventTrackingValue) {
    const { scopedObjectId } = this.taskWorkspaceStore.state();
    let objectIdsToSelect: number[] = [];
    if (isDefined(scopedObjectId)) {
      const parentObjectId =
        this.objectScopingService.getRootParentObjectId(scopedObjectId);
      assert(
        isDefined(parentObjectId),
        'Parent object id must be defined for a scoped object',
      );
      objectIdsToSelect = [parentObjectId];
      // should unscope to root level
      this.objectScopingService.scopeObject(
        undefined,
        'keyboard_shortcut',
        'unscope',
      );
    }

    this.getMinionForSelectedOutput().selectionService.setSelection(
      objectIdsToSelect,
      [],
      eventTrackingValue,
    );
  }
}
